<template>
  <div class="section">
    <div class="container">
      <!-- loading indicator -->
      <b-loading :is-full-page="true" :active="isFetching"></b-loading>

      <!-- logo and branding -->
      <div class="logo  has-text-centered">
        <h2 class="subtitle">Welcome to</h2>
        <h1 class="title">
          <img src="../assets/truckl-logo.png"/>
        </h1>
        <h2 class="subtitle ">
          The Road to Trust
        </h2>
      </div>


      <div class="main-content" v-if="!isFetching">

        <!-- error message if details failed to load -->
        <b-notification type="is-warning" class="error-box" v-if="showError" :closable="false" :has-icon="true" icon-pack="fas">
          There was an error retrieving the transaction details.
          Please check your link or contact support for further assistance.
        </b-notification>

        <template  v-if="!showError">
          <div class="columns is-centered">
            <div class="column is-one-quarter has-text-centered">
              <div class="card transaction-id-card">
                <div class="card-header">
                  <h3 class="card-header-title">Transaction ID</h3>
                </div>
                <div class="card-content">
                  {{ details.companyJobId }}
                </div>
              </div>
            </div>

            <div class="column is-one-quarter has-text-centered">
              <div class="card transaction-id-card">
                <div class="card-header">
                  <h3 class="card-header-title">Status</h3>
                </div>
                <div class="card-content visible-status">
                  {{ visibleStatus }}
                </div>
              </div>
            </div>

            <div class="column is-one-quarter has-text-centered">
              <div class="card transaction-id-card">
                <div class="card-header">
                  <h3 class="card-header-title">Planned Arrival</h3>
                </div>
                <div class="card-content">
                  {{ plannedArrival }}
                </div>
              </div>
            </div>

            <div class="column is-one-quarter has-text-centered">
              <div class="card transaction-id-card">
                <div class="card-header">
                  <h3 class="card-header-title">Estimated Arrival</h3>
                </div>
                <div class="card-content">
                  {{ estimatedArrival }}
                </div>
              </div>
            </div>

          </div>


          <!--
          <div class="columns is-multiline">
            <div class="column is-full">
              <div class="card">
                <div class="card-header">
                  <h3 class="card-header-title">Pick-up</h3>
                </div>
                <div class="card-content">
                  <point-details :point="point" v-for="point in origins" :key="point.companyJobPointKey"/>
                </div>
              </div>
            </div>

            <div class="column is-full">
              <div class="card">
                <div class="card-header">
                  <h3 class="card-header-title">Drop-off</h3>
                </div>
                <div class="card-content">
                  <point-details :point="point" v-for="point in destinations" :key="point.companyJobPointKey" />
                </div>
              </div>
            </div>

          </div>
          -->
        </template>
      </div>

      <footer class="footer">
        <div class="content">
          <p class="is-size-7 is-pulled-left">
            Copyright © 2018 Truckl LLC - All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import JobApi from '@/api-interface/companyjob'
import PointDetails from '@/components/TransactionDetails/JobPointInformation'
import { format } from 'date-fns'
import { isEmpty, isUndefined } from 'lodash-es'

export default {
  name: 'PublicTransactionInfo',

  created() {
    this.companyJobKey = this.$route.params.companyJobKey
    this.fetch()
  },

  data() {
    return {
      companyJobKey: null,
      isFetching: false,
      showError: false,
      details: {}
    }
  },

  computed: {
    visibleStatus() {
      // since we do not want to publicly display all statuses... we only show those in the "whitelist"

      /*
      const allowed = ['invites sent', 'origin check-in', 'in transit', 'destination check-in', 'finish job']
      const currentStatus = this.details.companyJobStatus.toLowerCase()
      let visibleStatus = 'N/A'
      if (allowed.includes(currentStatus)) {
        visibleStatus = currentStatus
      }

      return visibleStatus
      */

      // temporarily displaying all statuses
      return this.details.companyJobStatus
    },

    formattedJobPoints() {
      return this.details.companyJobPoints.map((point) => {
        let companyJobPointDate = point.companyJobPointDateEarliest
        if (point.companyJobPointDateLatest) {
          companyJobPointDate += ` to ${point.companyJobPointDateLatest}`
        }

        // guard against null values by setting some defaults
        const jobLocation = point.companyRolodexJobLocation || {
          companyRolodexJobLocationAddresses: [{}]
        }

        delete point.CompanyRolodexContactKey1
        delete point.CompanyRolodexContactKey2

        return {
          ...point,
          address: {
            ...jobLocation.companyRolodexJobLocationAddresses[0],
            facilityName: jobLocation.companyRolodexJobLocationName,
            street1: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressStreet1,
            street2: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressStreet2,
            zip: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressZip,
            state: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressState,
            city: jobLocation.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressCity
          },
          companyJobPointDate
        }
      })
    },
    origins() {
      return this.formattedJobPoints.filter((point) => point.companyJobPointType === 'origin')
    },

    destinations() {
      return this.formattedJobPoints.filter((point) => point.companyJobPointType === 'destination')
    },

    plannedArrival() {
      const dateFormat = 'MMM DD, YYYY'
      const timeFormat = 'hh:mm A'
      const firstDestination = this.destinations[0] || {}

      const earliestDate = format(firstDestination.companyJobPointDateEarliest, dateFormat)
      const latestDate = format(firstDestination.companyJobPointDateLatest, dateFormat)
      const earliestTime = format(firstDestination.companyJobPointDateEarliest, timeFormat)
      const latestTime = format(firstDestination.companyJobPointDateLatest, timeFormat)

      const time = `${earliestTime} - ${latestTime}`

      let formattedDateTime = 'N/A'

      if (!isEmpty(firstDestination) && !isUndefined(firstDestination)) {
        formattedDateTime = earliestDate

        if (earliestDate !== latestDate) {
          formattedDateTime = `${earliestDate} - ${latestDate}`
        }

        formattedDateTime = `${formattedDateTime}, ${time}`
      }

      return formattedDateTime
    },

    estimatedArrival() {
      const dateFormat = 'MMM DD, YYYY hh:mm A'
      const eta = this.details.companyJobEstimatedTimeOfArrival
      let formattedDate = 'N/A'

      if (eta) {
        formattedDate = format(eta, dateFormat)
      }

      return formattedDate
    }
  },

  components: {
    PointDetails
  },

  methods: {
    async fetch() {
      this.isFetching = true
      try {
        this.details = await JobApi.getPublicInformationByKey(this.companyJobKey)
      } catch (error) {
        console.error(error)
        this.showError = true
      } finally {
        this.isFetching = false
      }
    }
  }
}
</script>


<style scoped lang="scss">
.logo img {
  width: 300px;
}

.error-box {
  max-width: 500px;
  width: 90vw;
  margin: auto;
}

.section {
  min-height: 100vh;

  &::after {
    content: '';
    background-image: url('../assets/backgrounds/shutterstock_529683889.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    opacity: 0.5;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: fixed;
    z-index: -1;
  }
}

.main-content {
  margin-top: 80px;
  margin-bottom: 80px;
}

.visible-status {
  text-transform: capitalize;
}

.transaction-id-card .card-header-title {
  display: block;
}

.footer {
  padding: 1.5rem;
  width: 100%;
  background-color: transparent;
}
</style>
